import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { admin } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import toast from 'react-hot-toast';
import SupportAccessDialog from '../components/SupportAccessDialog';
import {
  Search,
  UserCheck,
  UserX,
  Key,
  Trash2,
  MoreVertical,
  Filter,
  X,
  Eye,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Download,
  LogIn
} from 'lucide-react';

const formatDuration = seconds => {
  if (!seconds) return '0m';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const personName = user => user?.contactName || user?.email || user?.companyName || 'Unnamed user';

const AdminUsers = () => {
  const { isSuperAdmin, user: currentAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState(searchParams.get('owner') || '');
  const [segmentFilter, setSegmentFilter] = useState(searchParams.get('segment') || '');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showActions, setShowActions] = useState(null);
  const [supportTarget, setSupportTarget] = useState(null);

  useEffect(() => {
    admin.getAdmins()
      .then(response => setAdmins(response.data.admins))
      .catch(error => {
        console.error('Error fetching administrators:', error);
        toast.error('Failed to load administrators');
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const debounce = setTimeout(() => {
      fetchUsers(controller.signal);
    }, 300);
    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  }, [searchTerm, statusFilter, ownerFilter, segmentFilter]);

  const fetchUsers = async (signal) => {
    try {
      const response = await admin.getUsers({ search: searchTerm, status: statusFilter, assignedAdminId: ownerFilter, segment: segmentFilter }, { signal });
      setUsers(response.data.users);
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return;
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userId = searchParams.get('user');
    if (userId && userId !== selectedUser) handleViewDetails(userId, false);
  }, [searchParams]);

  const handleReassign = async (userId, adminId) => {
    try {
      const response = await admin.reassignUser(userId, adminId);
      toast.success(response.data.message);
      await fetchUsers();
      if (selectedUser === userId) {
        if (!isSuperAdmin && adminId !== currentAdmin?.id) closeDetails();
        else await loadUserDetails(userId);
      }
    } catch (error) {
      console.error('Reassignment error:', error);
      toast.error(error.response?.data?.error || 'Failed to reassign user');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await admin.toggleUserStatus(userId, !currentStatus);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      await fetchUsers();
      if (selectedUser === userId) await loadUserDetails(userId);
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update user status');
    }
    setShowActions(null);
  };

  const handleSendPasswordReset = async (userId) => {
    try {
      await admin.sendPasswordReset(userId);
      toast.success('Password reset email sent');
    } catch (error) {
      console.error('Error sending reset:', error);
      toast.error('Failed to send password reset');
    }
    setShowActions(null);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await admin.deleteUser(userId);
      toast.success('User deleted');
      await fetchUsers();
      if (selectedUser === userId) closeDetails();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
    setShowActions(null);
  };

  const loadUserDetails = async (userId) => {
    setDetailLoading(true);
    try {
      const response = await admin.getUser(userId);
      setUserDetail(response.data);
    } catch (error) {
      console.error('Error fetching user detail:', error);
      toast.error(error.response?.data?.error || 'Failed to load user details');
      setSelectedUser(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetails = async (userId, updateUrl = true) => {
    setShowActions(null);
    setSelectedUser(userId);
    if (updateUrl) {
      const next = new URLSearchParams(searchParams);
      next.set('user', userId);
      setSearchParams(next, { replace: true });
    }
    await loadUserDetails(userId);
  };

  const closeDetails = () => {
    setSelectedUser(null);
    setUserDetail(null);
    const next = new URLSearchParams(searchParams);
    next.delete('user');
    setSearchParams(next, { replace: true });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 font-display">User Management</h1>
        <p className="text-gray-600 mt-2">Manage customer accounts, ownership, and access</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className={`grid grid-cols-1 gap-4 ${isSuperAdmin ? 'lg:grid-cols-[minmax(0,1fr)_170px_190px_220px]' : 'lg:grid-cols-[minmax(0,1fr)_180px_220px]'}`}>
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-12"
            />
          </div>
          <div className="relative min-w-[160px]">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field pl-11 appearance-none cursor-pointer"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <select value={segmentFilter} onChange={event => setSegmentFilter(event.target.value)} className="input-field cursor-pointer" aria-label="User activity segment">
            <option value="">All activity</option>
            <option value="new">New in 30 days</option>
            <option value="active30">Active in 30 days</option>
            <option value="inactive30">Inactive 30+ days</option>
            <option value="inactive60">Inactive 60+ days</option>
            <option value="never">Never logged in</option>
          </select>
          {isSuperAdmin && <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="input-field cursor-pointer"
            aria-label="Assigned administrator"
          >
            <option value="">All administrators</option>
            <option value="unassigned">Unassigned users</option>
            {admins.map(administrator => (
              <option key={administrator.id} value={administrator.id}>{administrator.name} ({administrator.userCount})</option>
            ))}
          </select>}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Stats
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Assigned Admin
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <button type="button" onClick={() => handleViewDetails(user.id)} aria-label={`View ${personName(user)} at ${user.companyName}`} className="flex items-center gap-3 text-left rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-500 font-semibold">
                            {personName(user)[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{personName(user)}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800">{user.companyName}</p>
                      <p className="text-sm text-gray-500">{user.phone || '-'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">
                          <strong>{user.exportCount ?? user.downloadCount}</strong> exports
                        </span>
                        <span className="text-gray-600">
                          <strong>{user.loginCount}</strong> logins
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        user.isActive 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {user.isActive ? <UserCheck size={12} /> : <UserX size={12} />}
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={user.assignedAdmin?.id || ''}
                        onChange={event => handleReassign(user.id, event.target.value)}
                        className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        aria-label={`Assigned administrator for ${personName(user)}`}
                      >
                        <option value="" disabled>Choose admin</option>
                        {admins.map(administrator => <option key={administrator.id} value={administrator.id}>{administrator.name}</option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.lastLogin 
                        ? new Date(user.lastLogin).toLocaleDateString() 
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      <button
                        onClick={() => setShowActions(showActions === user.id ? null : user.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical size={18} className="text-gray-500" />
                      </button>
                      
                      {showActions === user.id && (
                        <div className="absolute right-6 top-12 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-10 min-w-[180px]">
                          <button
                            onClick={() => handleViewDetails(user.id)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Eye size={16} />
                            View Details
                          </button>
                          <hr className="my-2" />
                          <button
                            onClick={() => handleToggleStatus(user.id, user.isActive)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            {user.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleSendPasswordReset(user.id)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Key size={16} />
                            Send Password Reset
                          </button>
                          <hr className="my-2" />
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 size={16} />
                            Delete User
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {showActions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActions(null)}
        ></div>
      )}

      {/* User detail modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeDetails}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto animate-fade-in">
            {detailLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
              </div>
            ) : userDetail ? (
              <>
                <div className="flex items-start justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
                  <div>
                    <div className="flex flex-wrap items-center gap-2"><h2 className="text-xl font-bold text-gray-900 font-display">{personName(userDetail.user)}</h2><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${userDetail.user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{userDetail.user.isActive ? 'Active' : 'Suspended'}</span></div>
                    <p className="text-sm font-medium text-gray-600">{userDetail.user.companyName}</p>
                    <p className="text-xs text-gray-500">{userDetail.user.email}</p>
                  </div>
                  <button onClick={closeDetails} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Account controls */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Account controls</h3>
                    <div className="flex flex-wrap gap-2">
                      {currentAdmin?.canViewAs && <button type="button" disabled={!userDetail.user.isActive} onClick={() => setSupportTarget({ id: userDetail.user.id, name: personName(userDetail.user), email: userDetail.user.email })} className="btn-primary inline-flex items-center gap-2 px-3 py-2 text-sm disabled:opacity-50"><Eye size={16} />View as user</button>}
                      <button type="button" onClick={() => handleToggleStatus(userDetail.user.id, userDetail.user.isActive)} className="btn-outline inline-flex items-center gap-2 px-3 py-2 text-sm">{userDetail.user.isActive ? <UserX size={16} /> : <UserCheck size={16} />}{userDetail.user.isActive ? 'Suspend access' : 'Restore access'}</button>
                      <button type="button" onClick={() => handleSendPasswordReset(userDetail.user.id)} className="btn-outline inline-flex items-center gap-2 px-3 py-2 text-sm"><Key size={16} /> Send password reset</button>
                      <button type="button" onClick={() => handleDeleteUser(userDetail.user.id)} className="btn-danger-ghost inline-flex items-center gap-2 px-3 py-2 text-sm"><Trash2 size={16} /> Delete account</button>
                    </div>
                    <label className="mt-3 flex max-w-sm flex-col gap-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Assigned manager
                      <select value={userDetail.user.assignedAdmin?.id || ''} onChange={event => handleReassign(userDetail.user.id, event.target.value)} className="input-field text-sm font-normal normal-case tracking-normal">
                        <option value="" disabled>Choose manager</option>
                        {admins.map(administrator => <option key={administrator.id} value={administrator.id}>{administrator.name}</option>)}
                      </select>
                    </label>
                  </div>

                  {/* Usage summary */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Usage overview</h3>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <div className="rounded-xl bg-primary-50 p-3"><p className="text-2xl font-bold text-primary-700">{userDetail.usageSummary?.logins || 0}</p><p className="text-xs font-medium text-gray-600">Logins</p></div>
                      <div className="rounded-xl bg-primary-50 p-3"><p className="text-2xl font-bold text-primary-700">{userDetail.usageSummary?.exports || 0}</p><p className="text-xs font-medium text-gray-600">Exports</p></div>
                      <div className="rounded-xl bg-primary-50 p-3"><p className="text-2xl font-bold text-primary-700">{userDetail.usageSummary?.views || 0}</p><p className="text-xs font-medium text-gray-600">Flyer views</p></div>
                      <div className="rounded-xl bg-primary-50 p-3"><p className="text-2xl font-bold text-primary-700">{formatDuration(userDetail.usageSummary?.pageSeconds)}</p><p className="text-xs font-medium text-gray-600">Time in Hub</p></div>
                    </div>
                    <p className="mt-3 text-sm text-gray-600">Export mix: <strong>{userDetail.usageSummary?.downloads || 0}</strong> downloads · <strong>{userDetail.usageSummary?.emails || 0}</strong> emails · <strong>{userDetail.usageSummary?.shares || 0}</strong> shares · <strong>{userDetail.usageSummary?.captionCopies || 0}</strong> caption copies</p>
                    <p className="mt-1 text-xs text-gray-500">Last recorded activity: {userDetail.usageSummary?.lastActivity ? new Date(userDetail.usageSummary.lastActivity).toLocaleString() : 'None yet'}</p>
                    {userDetail.topContent?.length > 0 && <div className="mt-4"><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Most-used content</p><div className="flex flex-wrap gap-2">{userDetail.topContent.map(item => <Link key={item.id} to={`/flyers/${item.id}`} className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700">{item.title} <strong>({item.count})</strong></Link>)}</div></div>}
                  </div>

                  {/* Profile */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Profile</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Mail size={16} className="text-gray-400 flex-shrink-0" /> {userDetail.user.email}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Phone size={16} className="text-gray-400 flex-shrink-0" /> {userDetail.user.phone || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
                        <MapPin size={16} className="text-gray-400 flex-shrink-0" /> {userDetail.user.address || '-'}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Calendar size={16} className="text-gray-400 flex-shrink-0" />
                        Joined {new Date(userDetail.user.createdAt).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Shield size={16} className="text-gray-400 flex-shrink-0" />
                        Assigned to {userDetail.user.assignedAdmin?.name || 'no administrator'}
                      </div>
                    </div>
                    {(userDetail.user.branding?.primaryColor || userDetail.user.branding?.logoUrl) && (
                      <div className="mt-4 flex items-center gap-3 text-sm text-gray-500">
                        <span>Brand colors:</span>
                        <div className="flex gap-1">
                          <div
                            className="w-6 h-6 rounded border border-gray-200"
                            style={{ backgroundColor: userDetail.user.branding.primaryColor }}
                            title={userDetail.user.branding.primaryColor}
                          ></div>
                          <div
                            className="w-6 h-6 rounded border border-gray-200"
                            style={{ backgroundColor: userDetail.user.branding.secondaryColor }}
                            title={userDetail.user.branding.secondaryColor}
                          ></div>
                        </div>
                        {userDetail.user.branding?.logoUrl && (
                          <span className="text-gray-400">Logo uploaded</span>
                        )}
                        {userDetail.user.branding?.logoMode === 'wordmark' && (
                          <span className="text-gray-400">Company-name text logo</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Recent Activity */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Recent Activity</h3>
                    {userDetail.recentActivity.length === 0 ? (
                      <p className="text-sm text-gray-400">No activity yet</p>
                    ) : (
                      <div className="space-y-2">
                        {userDetail.recentActivity.map((a, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                            <div className="flex items-center gap-2 text-gray-700">
                              <Download size={14} className="text-gray-400 flex-shrink-0" />
                              {{ email: 'Emailed', share: 'Shared', download: 'Downloaded', flyer_view: 'Viewed', caption_copy: 'Copied caption' }[a.activityType] || 'Used'}{a.flyerTitle ? ` “${a.flyerTitle}”` : ''}{a.sizeName ? ` (${a.sizeName})` : ''}
                            </div>
                            <span className="text-gray-400 flex-shrink-0 ml-2">{new Date(a.createdAt).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Login History */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Login History</h3>
                    {userDetail.loginHistory.length === 0 ? (
                      <p className="text-sm text-gray-400">No login history yet</p>
                    ) : (
                      <div className="space-y-2">
                        {userDetail.loginHistory.map((l, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                            <div className="flex items-center gap-2 text-gray-700">
                              <LogIn size={14} className="text-gray-400 flex-shrink-0" />
                              {l.ipAddress || 'Unknown IP'}
                            </div>
                            <span className="text-gray-400 flex-shrink-0 ml-2">{new Date(l.createdAt).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
      {supportTarget && <SupportAccessDialog target={supportTarget} onClose={() => setSupportTarget(null)} />}
    </div>
  );
};

export default AdminUsers;
